import { useMemo, useState, useEffect } from "react";
import { fmtMoneyDetail, fmtNumber } from "@/lib/simulator";
import {
  INITIAL_RIPPLE, RIPPLE_WAVE_RATE, RIPPLE_WAVE_TIERS, DROP_MATCH,
  CAP_PAYMENTS_PER_ORDER, packageById,
} from "@/lib/compPlan";
import { CaretRight, Wallet, Coins, Crown, Sparkle } from "@phosphor-icons/react";

/**
 * Level-by-level Order Flow simulator — the user picks the level at which a
 * hypothetical order lands and sees the 3-payment cap resolve in real time.
 *
 * Engine ordering, faithful to the spec:
 *   Slot 0  : Drop Match / Points  (uncapped, always fires; L1..L3 only)
 *   Slot 1..3 : Direct  ->  Rank  ->  Check Match  (shared 3-slot budget)
 */
export default function OrderFlow({ rankIdx, myPkgName, downlinePkgId }) {
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [step, setStep] = useState(0);   // 0=nothing, 1=drop match, 2=direct, 3=rank, 4=cm, 5=done
  const [tick, setTick] = useState(0);   // rerender key for animation restart

  // Autoplay reveal
  useEffect(() => {
    setStep(0);
    const ids = [];
    [1, 2, 3, 4, 5].forEach((s, i) => {
      ids.push(setTimeout(() => setStep(s), 200 + i * 500));
    });
    return () => ids.forEach(clearTimeout);
  }, [selectedLevel, tick]);

  const flow = useMemo(() => {
    const L = selectedLevel;
    const R = rankIdx;
    const pkg = packageById(downlinePkgId);
    const orderValue = pkg.price;
    const orderPoints = pkg.points;

    // Drop Match / Points (uncapped, L1..L3 only)
    const dmRate = L >= 1 && L <= 3 ? DROP_MATCH[L - 1] : 0;
    const dmShares = orderPoints * dmRate;

    // Direct
    let directRate = 0, directSlot = 0;
    if (L >= 1 && L <= 5 && (R + 1) >= L) {
      directRate = INITIAL_RIPPLE[L - 1]; directSlot = 1;
    }
    let slotsLeft = CAP_PAYMENTS_PER_ORDER - directSlot;

    // Rank (Ripple Wave): L1..L11 for R6+, 1% per unlocked tier, cap-aware.
    let rankRate = 0, rankSlots = 0;
    if (L >= 1 && L <= 11 && slotsLeft > 0 && R >= 5) {
      const rwTiers = Math.min(RIPPLE_WAVE_TIERS, R - 4);
      rankSlots = Math.min(rwTiers, slotsLeft);
      rankRate = rankSlots * RIPPLE_WAVE_RATE;
    }
    slotsLeft -= rankSlots;

    // Deep Ripple: L12..L20 only, R12+.
    let cmRate = 0, cmSlot = 0;
    if (L >= 12 && L <= 20 && R >= 11 && slotsLeft >= 1) {
      cmRate = (((R - 11) % 3) + 1) * 0.003; cmSlot = 1;
    }

    return {
      L, orderValue, orderPoints, pkgName: pkg.name,
      dmRate, dmShares,
      directRate, directPay: orderValue * directRate, directSlot,
      rankRate, rankPay: orderValue * rankRate, rankSlots,
      cmRate, cmPay: orderValue * cmRate, cmSlot,
      totalCash: orderValue * (directRate + rankRate + cmRate),
      slotsUsed: directSlot + rankSlots + cmSlot,
    };
  }, [selectedLevel, rankIdx, downlinePkgId]);

  return (
    <div className="glass rounded-2xl p-5 md:p-6 fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400">Order flow simulator</div>
          <div className="text-sm text-slate-300 mt-0.5">
            Pick a level. Watch a single {flow.pkgName} order pay you across the streams.
          </div>
        </div>
        <button
          data-testid="sim-orderflow-replay"
          onClick={() => setTick((t) => t + 1)}
          className="text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/25"
        >
          Replay ↻
        </button>
      </div>

      {/* Level picker */}
      <div className="grid grid-cols-10 md:grid-cols-20 gap-1 mb-6" data-testid="sim-orderflow-levels">
        {Array.from({ length: 20 }).map((_, i) => {
          const L = i + 1;
          const active = L === selectedLevel;
          return (
            <button
              key={L}
              data-testid={`sim-orderflow-level-${L}`}
              onClick={() => setSelectedLevel(L)}
              className={`aspect-square rounded-md font-mono text-[11px] transition-all ${
                active
                  ? "bg-gradient-to-br from-orange-500 to-pink-500 text-white shadow-lg shadow-pink-500/40 scale-110"
                  : "bg-white/[0.04] text-slate-400 hover:bg-white/[0.08] hover:text-white"
              }`}
              title={`Order at Level ${L}`}
            >
              L{L}
            </button>
          );
        })}
      </div>

      {/* Order card and flow */}
      <div className="grid md:grid-cols-12 gap-4">
        {/* Left: order */}
        <div className="md:col-span-4">
          <div className="glass rounded-xl p-4 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 pointer-events-none"
                 style={{ background: "radial-gradient(circle at top left,#EC4899,transparent 65%)" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Incoming order</div>
              <div className="mt-2 flex items-baseline gap-2">
                <Wallet weight="duotone" className="h-5 w-5 text-orange-300" />
                <span data-testid="sim-orderflow-order-value" className="font-mono text-2xl font-semibold text-white">
                  ${flow.orderValue.toLocaleString()}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                {flow.pkgName} · {flow.orderPoints} points · placed at L{flow.L}
              </div>
              <div className="mt-4 text-[11px] text-slate-500">
                Slots used: <span className="text-slate-200 font-mono">{flow.slotsUsed}/3</span>
                <span className="text-slate-500 ml-2">Drop Match uncapped</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: streams */}
        <div className="md:col-span-8 space-y-2">
          {/* Drop Match (Points) — always shows if L<=3 */}
          <FlowRow
            visible={step >= 1}
            active={flow.dmShares > 0}
            color="sky"
            icon={Sparkle}
            label="Drop Match"
            sub={flow.L <= 3
              ? `${(flow.dmRate * 100)}% of ${flow.orderPoints} points (L${flow.L}) · uncapped`
              : "Not applicable — L4+ orders don't hit Drop Match"}
            value={flow.dmShares > 0 ? `+${fmtNumber(flow.dmShares)} shares` : "—"}
            slotBadge={null}
            testId="sim-orderflow-dm"
          />
          <FlowRow
            visible={step >= 2}
            active={flow.directPay > 0}
            color="orange"
            icon={Coins}
            label="Initial Ripple (Direct)"
            sub={flow.directRate > 0
              ? `${(flow.directRate * 100)}% × $${flow.orderValue} (tier ${flow.L}) · slot 1`
              : (flow.L > 5 ? "Not applicable — L6+ orders bypass Direct" : `Locked — need rank R${flow.L}`)}
            value={flow.directPay > 0 ? `+${fmtMoneyDetail(flow.directPay)}` : "—"}
            slotBadge={flow.directSlot > 0 ? "slot 1" : null}
            testId="sim-orderflow-ir"
          />
          <FlowRow
            visible={step >= 3}
            active={flow.rankPay > 0}
            color="pink"
            icon={CaretRight}
            label="Ripple Wave (Rank)"
            sub={flow.L > 11
              ? "Not applicable — L12+ pays Deep Ripple"
              : (rankIdx < 5
                ? "Locked — reach R6 to unlock"
                : (flow.rankSlots > 0
                  ? `${flow.rankSlots}× 1% (tiers 1–${flow.rankSlots}) · 3-payment cap`
                  : "No slots left after Initial Ripple + cap"))}
            value={flow.rankPay > 0 ? `+${fmtMoneyDetail(flow.rankPay)}` : "—"}
            slotBadge={flow.rankSlots > 0 ? `slot${flow.rankSlots > 1 ? "s" : ""} ${flow.directSlot + 1}${flow.rankSlots > 1 ? "-" + (flow.directSlot + flow.rankSlots) : ""}` : null}
            testId="sim-orderflow-rw"
          />
          <FlowRow
            visible={step >= 4}
            active={flow.cmPay > 0}
            color="purple"
            icon={Crown}
            label="Deep Ripple (Check Match)"
            sub={rankIdx < 11
              ? "Locked — reach R12"
              : (flow.L < 12
                ? "Not applicable — Deep Ripple only pays on L12-L20"
                : (flow.cmSlot > 0
                  ? `${(flow.cmRate * 100).toFixed(1)}% × $${flow.orderValue} · group position`
                  : "No slots left after Direct + Rank"))}
            value={flow.cmPay > 0 ? `+${fmtMoneyDetail(flow.cmPay)}` : "—"}
            slotBadge={flow.cmSlot > 0 ? `slot ${flow.directSlot + flow.rankSlots + 1}` : null}
            testId="sim-orderflow-dr"
          />

          {/* Total */}
          <div
            className={`glass rounded-xl p-4 mt-3 grad-border transition-all ${step >= 5 ? "opacity-100" : "opacity-30"}`}
            data-testid="sim-orderflow-total"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Your take from this one order</div>
                <div className="mt-1 font-mono text-3xl font-semibold grad-text">
                  {fmtMoneyDetail(flow.totalCash)}
                </div>
                {flow.dmShares > 0 && (
                  <div className="text-[11px] text-slate-400 font-mono mt-1">
                    + <span className="text-sky-300">{fmtNumber(flow.dmShares)} shares</span> via Drop Match
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Payment rate</div>
                <div className="mt-1 font-mono text-lg text-slate-200">
                  {((flow.directRate + flow.rankRate + flow.cmRate) * 100).toFixed(3)}%
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  as leader {myPkgName} · rank R{rankIdx >= 0 ? rankIdx + 1 : 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const COLOR = {
  orange: { dot: "#F97316", grad: "from-orange-400 to-orange-200" },
  pink:   { dot: "#EC4899", grad: "from-pink-400 to-pink-200" },
  purple: { dot: "#A855F7", grad: "from-purple-400 to-purple-200" },
  sky:    { dot: "#38BDF8", grad: "from-sky-300 to-cyan-200" },
};

function FlowRow({ visible, active, color = "orange", icon: Icon, label, sub, value, slotBadge, testId }) {
  const c = COLOR[color];
  return (
    <div
      data-testid={testId}
      className={`glass rounded-xl px-4 py-3 flex items-center gap-3 transition-all duration-500 ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
      } ${active ? "border-white/15" : "opacity-60"}`}
    >
      <div
        className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${c.dot}22`, boxShadow: active ? `0 0 20px ${c.dot}66` : "none" }}
      >
        <Icon weight="duotone" className="h-4 w-4" style={{ color: c.dot }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white font-medium">{label}</span>
          {slotBadge && (
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10 text-slate-300">
              {slotBadge}
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 truncate">{sub}</div>
      </div>
      <div className={`font-mono text-base font-semibold text-transparent bg-clip-text bg-gradient-to-r ${c.grad} ${active ? "" : "opacity-40"}`}>
        {value}
      </div>
    </div>
  );
}
